// Ultimate Card Game (UCG) - Building Attachment Rendering
//
// Extracted from js/engine/renderer.js. Mixed onto Renderer.prototype (see
// the Object.assign() call at the bottom of renderer.js) rather than kept
// as free functions - every method here reads instance state (this.*) or
// calls a sibling method (this.renderBuildingCable() etc.) exactly like
import { MODULE_TOP_D, MODULE_SKEW } from '../modules.js';

// Building attachment cables are always drawn from a fixed 4-point path (see
// renderBuildingCables() below), so 3 segment lengths is always enough -
// pre-allocated once instead of a fresh array per cable per frame
// (Invariante #1).
const _cableSegLen = [0, 0, 0];

// Toxin cloud radial gradient, baked once at a fixed reference radius
// (Paradigm 8: no createRadialGradient() per cloud per frame). Every cloud's
// per-frame `fade` only scales every color stop's alpha by the same factor,
// so it's applied afterwards via ctx.globalAlpha instead of needing its own
// bake; the per-cloud radius is applied via ctx.scale() before drawImage().
const TOXIN_GRAD_REF_R = 64;
let _toxinGradCanvas = null;
function getToxinGradCanvas() {
  if (_toxinGradCanvas) return _toxinGradCanvas;
  const size = TOXIN_GRAD_REF_R * 2;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const gctx = canvas.getContext('2d');
  const grad = gctx.createRadialGradient(TOXIN_GRAD_REF_R, TOXIN_GRAD_REF_R, 4, TOXIN_GRAD_REF_R, TOXIN_GRAD_REF_R, TOXIN_GRAD_REF_R);
  grad.addColorStop(0, 'rgba(132, 204, 22, 0.32)');
  grad.addColorStop(0.6, 'rgba(77, 124, 15, 0.22)');
  grad.addColorStop(1, 'rgba(46, 74, 9, 0)');
  gctx.fillStyle = grad;
  gctx.fillRect(0, 0, size, size);
  _toxinGradCanvas = canvas;
  return canvas;
}

// Generic helper for the same LUT trick: bakes a centered radial gradient at
// a fixed reference radius into an offscreen canvas. Callers scale by
// (liveRadius / refR) and set ctx.globalAlpha for any color-stop alpha that
// was a uniform multiple of a per-frame pulse value, instead of calling
// createRadialGradient() with recomputed stop colors every frame.
function bakeRadialGradientCanvas(refR, stops) {
  const size = refR * 2;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const gctx = canvas.getContext('2d');
  const grad = gctx.createRadialGradient(refR, refR, stops.innerR || 0, refR, refR, refR);
  for (const [offset, color] of stops.colors) grad.addColorStop(offset, color);
  gctx.fillStyle = grad;
  if (typeof gctx.beginPath === 'function' && typeof gctx.arc === 'function' && typeof gctx.fill === 'function') {
    gctx.beginPath();
    gctx.arc(refR, refR, refR, 0, Math.PI * 2);
    gctx.fill();
  } else {
    gctx.fillRect(0, 0, size, size);
  }
  return canvas;
}

// Sul Base bio-plasma patch glow (see renderSulBase() biomassPatches loop):
// the "breathing" respiration pulse scales every stop's alpha by the exact
// same `alphaPulse` factor, so it's baked once at alphaPulse=1 and replayed
// with ctx.globalAlpha = alphaPulse (Paradigm 8, deterministic/render-only -
// nothing here reads or writes simulation state).
const SUL_SUBGLOW_REF_R = 64;
let _sulSubGlowGradCanvas = null;
function getSulSubGlowGradCanvas() {
  if (_sulSubGlowGradCanvas) return _sulSubGlowGradCanvas;
  _sulSubGlowGradCanvas = bakeRadialGradientCanvas(SUL_SUBGLOW_REF_R, {
    colors: [
      [0, 'rgba(244, 114, 182, 0.48)'],
      [0.45, 'rgba(217, 70, 239, 0.32)'],
      [0.78, 'rgba(147, 51, 234, 0.16)'],
      [1.0, 'rgba(30, 10, 45, 0)']
    ]
  });
  return _sulSubGlowGradCanvas;
}

const SUL_LOBE_REF_R = 64;
let _sulLobeGradCanvas = null;
function getSulLobeGradCanvas() {
  if (_sulLobeGradCanvas) return _sulLobeGradCanvas;
  _sulLobeGradCanvas = bakeRadialGradientCanvas(SUL_LOBE_REF_R, {
    colors: [
      [0, 'rgba(253, 230, 138, 0.55)'],
      [0.35, 'rgba(244, 114, 182, 0.45)'],
      [0.72, 'rgba(162, 28, 175, 0.30)'],
      [1.0, 'rgba(74, 4, 78, 0)']
    ]
  });
  return _sulLobeGradCanvas;
}

// Sul Base entrance portal glow - fully static color stops (no time
// dependency at all), baked once and replayed via drawImage + ellipse clip.
const SUL_PORTAL_REF_R = 64;
let _sulPortalGradCanvas = null;
function getSulPortalGradCanvas() {
  if (_sulPortalGradCanvas) return _sulPortalGradCanvas;
  _sulPortalGradCanvas = bakeRadialGradientCanvas(SUL_PORTAL_REF_R, {
    colors: [
      [0, '#ffffff'],
      [0.35, '#f472b6'],
      [0.7, '#a855f7'],
      [1, 'rgba(168, 85, 247, 0)']
    ]
  });
  return _sulPortalGradCanvas;
}

// Sul Base apex heat haze blur wash, ionization corona, and bio-plasma sphere body
// are baked once into reusable LUT canvases and replayed via drawImage with alpha scaling.
const SUL_HEAT_REF_R = 64;
let _sulHeatGradCanvas = null;
function getSulHeatGradCanvas() {
  if (_sulHeatGradCanvas) return _sulHeatGradCanvas;
  _sulHeatGradCanvas = bakeRadialGradientCanvas(SUL_HEAT_REF_R, {
    colors: [
      [0, 'rgba(254, 240, 138, 1.0)'],
      [0.35, 'rgba(244, 114, 182, 0.733)'],
      [0.70, 'rgba(217, 70, 239, 0.40)'],
      [1.0, 'rgba(147, 51, 234, 0)']
    ]
  });
  return _sulHeatGradCanvas;
}

const SUL_CORONA_REF_R = 64;
let _sulCoronaGradCanvas = null;
function getSulCoronaGradCanvas() {
  if (_sulCoronaGradCanvas) return _sulCoronaGradCanvas;
  _sulCoronaGradCanvas = bakeRadialGradientCanvas(SUL_CORONA_REF_R, {
    colors: [
      [0, 'rgba(255, 255, 255, 1.0)'],
      [0.3, 'rgba(240, 171, 252, 0.5)'],
      [0.7, 'rgba(217, 70, 239, 0.25)'],
      [1.0, 'rgba(147, 51, 234, 0)']
    ]
  });
  return _sulCoronaGradCanvas;
}

const SUL_SPHERE_REF_R = 64;
let _sulApexSphereIdleCanvas = null;
let _sulApexSphereChargedCanvas = null;
function getSulApexSphereGradCanvas(charged) {
  if (charged) {
    if (_sulApexSphereChargedCanvas) return _sulApexSphereChargedCanvas;
    _sulApexSphereChargedCanvas = bakeRadialGradientCanvas(SUL_SPHERE_REF_R, {
      colors: [
        [0, '#ffffff'],
        [0.3, '#fef08a'],
        [0.7, '#d946ef'],
        [1.0, '#7e22ce']
      ]
    });
    return _sulApexSphereChargedCanvas;
  }
  if (_sulApexSphereIdleCanvas) return _sulApexSphereIdleCanvas;
  _sulApexSphereIdleCanvas = bakeRadialGradientCanvas(SUL_SPHERE_REF_R, {
    colors: [
      [0, '#ffffff'],
      [0.3, '#f472b6'],
      [0.7, '#d946ef'],
      [1.0, '#7e22ce']
    ]
  });
  return _sulApexSphereIdleCanvas;
}

export const SUL_TIERS = Object.freeze([
  Object.freeze({ w: 140, h: 10,  d: 4.5 }), // Stufe 1 (Boden)
  Object.freeze({ w: 126, h: 9.5, d: 4.2 }), // Stufe 2
  Object.freeze({ w: 112, h: 9,   d: 3.9 }), // Stufe 3
  Object.freeze({ w: 98,  h: 8.5, d: 3.6 }), // Stufe 4
  Object.freeze({ w: 84,  h: 8.5, d: 3.3 }), // Stufe 5
  Object.freeze({ w: 70,  h: 8,   d: 3.0 }), // Stufe 6
  Object.freeze({ w: 56,  h: 8,   d: 2.7 }), // Stufe 7
  Object.freeze({ w: 42,  h: 8,   d: 2.4 })  // Stufe 8 (Apex-Plattform)
]);

const _sulTierCoordsScratch = Array.from({ length: 8 }, () => ({ x: 0, y: 0, w: 0, h: 0, d: 0, skew: 0 }));
export const SUL_STATUE_OFFSETS = Object.freeze([-17, 17]);

export const SUL_BIOMASS_PATCH_DEFS = Object.freeze([
  Object.freeze({
    tierIdx: 0, rel: 'left', offsetX: 18, offsetY: 3, rx: 22, ry: 10, seed: 0.0,
    lobes: Object.freeze([
      Object.freeze({ dx: -6, dy: 2, r: 12 }), Object.freeze({ dx: 6, dy: -2, r: 10 }),
      Object.freeze({ dx: 14, dy: 3, r: 8 }), Object.freeze({ dx: -12, dy: 4, r: 9 })
    ]),
    vessels: Object.freeze([
      Object.freeze([ Object.freeze({ x: -14, y: 5 }), Object.freeze({ x: -4, y: 1 }), Object.freeze({ x: 8, y: -3 }), Object.freeze({ x: 16, y: -1 }) ]),
      Object.freeze([ Object.freeze({ x: -2, y: 4 }), Object.freeze({ x: 5, y: 6 }), Object.freeze({ x: 12, y: 5 }) ])
    ]),
    spores: Object.freeze([ Object.freeze({ x: -5, y: 0, p: 0 }), Object.freeze({ x: 4, y: -2, p: 1.5 }), Object.freeze({ x: 11, y: 2, p: 3.0 }) ])
  }),
  Object.freeze({
    tierIdx: 1, rel: 'right', offsetX: -14, offsetY: 4, rx: 20, ry: 11, seed: 1.8,
    lobes: Object.freeze([
      Object.freeze({ dx: -8, dy: -1, r: 11 }), Object.freeze({ dx: 4, dy: 1, r: 12 }),
      Object.freeze({ dx: 12, dy: -2, r: 9 }), Object.freeze({ dx: -2, dy: 5, r: 8 })
    ]),
    vessels: Object.freeze([
      Object.freeze([ Object.freeze({ x: -10, y: 3 }), Object.freeze({ x: 0, y: 0 }), Object.freeze({ x: 10, y: -2 }), Object.freeze({ x: 18, y: 1 }) ])
    ]),
    spores: Object.freeze([ Object.freeze({ x: -4, y: 2, p: 0.8 }), Object.freeze({ x: 6, y: -1, p: 2.2 }), Object.freeze({ x: 14, y: 1, p: 4.1 }) ])
  }),
  Object.freeze({
    tierIdx: 2, rel: 'left', offsetX: 16, offsetY: -1, rx: 18, ry: 9, seed: 3.2,
    lobes: Object.freeze([
      Object.freeze({ dx: -5, dy: 1, r: 10 }), Object.freeze({ dx: 5, dy: -1, r: 9 }),
      Object.freeze({ dx: 12, dy: 2, r: 7 })
    ]),
    vessels: Object.freeze([
      Object.freeze([ Object.freeze({ x: -8, y: 3 }), Object.freeze({ x: 2, y: -1 }), Object.freeze({ x: 11, y: 0 }) ])
    ]),
    spores: Object.freeze([ Object.freeze({ x: -2, y: 1, p: 1.2 }), Object.freeze({ x: 7, y: -1, p: 2.9 }) ])
  }),
  Object.freeze({
    tierIdx: 1, rel: 'center', offsetX: -15, offsetY: -3, rx: 16, ry: 8, seed: 4.5,
    lobes: Object.freeze([
      Object.freeze({ dx: -4, dy: 0, r: 9 }), Object.freeze({ dx: 4, dy: 2, r: 8 }),
      Object.freeze({ dx: -1, dy: -3, r: 7 })
    ]),
    vessels: Object.freeze([
      Object.freeze([ Object.freeze({ x: -6, y: 1 }), Object.freeze({ x: 0, y: -2 }), Object.freeze({ x: 6, y: 1 }) ])
    ]),
    spores: Object.freeze([ Object.freeze({ x: -1, y: -1, p: 0.5 }), Object.freeze({ x: 4, y: 2, p: 2.0 }) ])
  }),
  Object.freeze({
    tierIdx: 4, rel: 'right', offsetX: -12, offsetY: -1, rx: 15, ry: 8, seed: 5.7,
    lobes: Object.freeze([
      Object.freeze({ dx: -4, dy: 1, r: 8 }), Object.freeze({ dx: 4, dy: -1, r: 8 }),
      Object.freeze({ dx: 9, dy: 1, r: 6 })
    ]),
    vessels: Object.freeze([
      Object.freeze([ Object.freeze({ x: -6, y: 2 }), Object.freeze({ x: 1, y: 0 }), Object.freeze({ x: 8, y: 1 }) ])
    ]),
    spores: Object.freeze([ Object.freeze({ x: -1, y: 1, p: 1.7 }), Object.freeze({ x: 6, y: 0, p: 3.6 }) ])
  })
]);

function traceCablePath(ctx, points) {
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length - 1; i++) {
    const midX = (points[i].x + points[i + 1].x) / 2;
    const midY = (points[i].y + points[i + 1].y) / 2;
    ctx.quadraticCurveTo(points[i].x, points[i].y, midX, midY);
  }
  ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
}

export const buildingsMixin = {
  // 3D Volumetric (Plastisch) 8-Bit Planetary Fortress Base Rendering
  // ============================================================
  // BASE ATTACHMENTS - built Building cards render flanking the fortress:
  // the first one built above it, the second below. Same small footprint
  // and shadow/highlight language on both teams so the design reads
  // correctly whether it's tucked against the west or east edge.
  // ============================================================
  // Cables only - called before renderBase() so the fortress body naturally
  // occludes wherever a cable's base-facing end runs into it.
  renderBuildingCables(ctx, battlefield, base, glowColor) {
    const bs = battlefield.buildingSystem;
    const built = bs?.built?.[base.team];
    if (!built || built.length === 0) return;
    const dir = base.team === 'western' ? 1 : -1;

    built.forEach((buildingId, slot) => {
      const pos = bs.getAttachmentPosition(base.team, buildingId);
      if (!pos) return;
      // The base-facing end of each cable reaches well past the hull's outer
      // edge, into the ground floor's front-face silhouette (roughly
      // x:base.x+-14 / y:base.y-4..+20 - the front face spans
      // FOUNDATION_ROOF_DY..base.y+36), instead of stopping right at the rim.
      // Since renderBase() is drawn after this, that overlap is what actually
      // hides the end of the cable behind the fortress.
      const points = slot === 0
        ? [
            { x: pos.x, y: pos.y + 20 },
            { x: pos.x - dir * 12, y: pos.y + 34 },
            { x: pos.x - dir * 12, y: base.y - 24 },
            { x: base.x + dir * 11, y: base.y + 3 }
          ]
        : [
            { x: base.x + dir * 11, y: base.y + 16 },
            { x: pos.x - dir * 12, y: base.y + 24 },
            { x: pos.x - dir * 12, y: pos.y - 32 },
            { x: pos.x, y: pos.y - 18 }
          ];
      this.renderBuildingCable(ctx, points, glowColor);
    });
  },

  // Attachment sprites only - called after renderBase() so the buildings
  // themselves still sit visually in front of the fortress.
  renderBuildingAttachmentSprites(ctx, battlefield, base, mainColor, glowColor) {
    const bs = battlefield.buildingSystem;
    const built = bs?.built?.[base.team];
    if (!built || built.length === 0) return;
    const dir = base.team === 'western' ? 1 : -1;

    built.forEach((buildingId) => {
      const pos = bs.getAttachmentPosition(base.team, buildingId);
      if (!pos) return;
      this.renderBuildingAttachment(ctx, battlefield, buildingId, pos.x, pos.y, dir, mainColor, glowColor, base.team);
    });
  },

  // Thick power cable linking a base attachment to the fortress body, with
  // 2-3 small energy pulses continuously travelling along it so the
  // connection reads as live infrastructure rather than a static pipe.
  // Corners are rounded via quadratic curves through each interior point
  // (instead of straight lineTo joins) so the cable reads as a flexible
  // conduit with no sharp elbow kinks, particularly where it bends into
  // the base.
  renderBuildingCable(ctx, points, glowColor) {
    const t = performance.now() * 0.001;
    ctx.save();

    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    // Cable body: dark insulation jacket + slightly lighter inner core.
    traceCablePath(ctx, points);
    ctx.strokeStyle = '#05070b';
    ctx.lineWidth = 8;
    ctx.stroke();
    traceCablePath(ctx, points);
    ctx.strokeStyle = '#243244';
    ctx.lineWidth = 5;
    ctx.stroke();

    // Cumulative segment lengths for even pulse-speed interpolation across bends.
    const segCount = points.length - 1;
    let total = 0;
    for (let i = 1; i < points.length; i++) {
      const d = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
      _cableSegLen[i - 1] = d;
      total += d;
    }

    const pulseCount = 3;
    for (let p = 0; p < pulseCount; p++) {
      const frac = (t * 0.35 + p / pulseCount) % 1;
      let dist = frac * total;
      let idx = 0;
      while (idx < segCount - 1 && dist > _cableSegLen[idx]) {
        dist -= _cableSegLen[idx];
        idx++;
      }
      const a = points[idx], b = points[idx + 1];
      const segT = _cableSegLen[idx] > 0 ? dist / _cableSegLen[idx] : 0;
      const px = a.x + (b.x - a.x) * segT;
      const py = a.y + (b.y - a.y) * segT;

      ctx.fillStyle = glowColor;
      ctx.beginPath();
      ctx.arc(px, py, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.beginPath();
      ctx.arc(px, py, 1.6, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  },

  renderBuildingAttachment(ctx, battlefield, buildingId, x, y, dir, mainColor, glowColor, team) {
    const t = performance.now() * 0.001;
    ctx.save();

    // Shared grounding shadow so every attachment reads as sitting on the
    // same ground plane as the fortress itself.
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.beginPath();
    ctx.ellipse(x, y + 20, 26, 9, 0, 0, Math.PI * 2);
    ctx.fill();

    const steelD = '#0f172a';
    const steel = '#1e293b';
    const steelL = '#475569';
    const highlight = '#cbd5e1';

    switch (buildingId) {
      case 'research_lab': {
        const bs = battlefield.buildingSystem;
        const ready = bs?.canFreeSwap(team);
        const blink = Math.floor(t * 3) % 2 === 0;
        const lampColor = ready ? (blink ? '#4ade80' : '#16a34a') : (blink ? '#facc15' : '#a16207');

        ctx.fillStyle = steel;
        ctx.fillRect(x - 16, y - 4, 32, 22);
        ctx.fillStyle = steelL;
        ctx.fillRect(x - 16, y - 4, 32, 4);
        ctx.fillStyle = steelD;
        ctx.fillRect(x - 18, y + 14, 36, 6);

        ctx.fillStyle = steelD;
        ctx.fillRect(x - 12, y - 20, 24, 18);
        ctx.fillStyle = '#0ea5b7';
        ctx.fillRect(x - 9, y - 16, 18, 4);
        ctx.fillStyle = lampColor;
        ctx.fillRect(x - 3, y - 26, 6, 6);
        ctx.fillStyle = highlight;
        ctx.fillRect(x - 12, y - 20, 24, 2);
        break;
      }

      case 'toxin_stack': {
        const flesh = '#4d1a52';
        const fleshD = '#2e0f33';
        const fleshL = '#6d2a73';
        const puffRise = (t * 18) % 24;

        ctx.fillStyle = fleshD;
        ctx.beginPath();
        ctx.ellipse(x, y + 8, 22, 12, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = flesh;
        ctx.fillRect(x - 16, y - 12, 32, 22);
        ctx.fillStyle = fleshL;
        ctx.fillRect(x - 10, y - 26, 20, 16);
        ctx.fillStyle = '#84cc16';
        ctx.fillRect(x - 3, y - 30 - 0, 6, 6);

        ctx.globalAlpha = Math.max(0, 1 - puffRise / 24);
        ctx.fillStyle = '#84cc16';
        ctx.beginPath();
        ctx.arc(x + Math.sin(t * 2) * 4, y - 34 - puffRise, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        break;
      }

      case 'emp_array': {
        const st = battlefield.buildingSystem?.state?.[team];
        const justPulsed = st && (st.empTimer || 0) < 0.3;
        const pulse = justPulsed ? 1 : 0.4 + 0.2 * Math.sin(t * 3);

        ctx.fillStyle = steelD;
        ctx.fillRect(x - 6, y - 4, 12, 22);
        ctx.save();
        ctx.translate(x, y - 10);
        ctx.rotate(-0.5 * dir);
        ctx.fillStyle = '#78350f';
        ctx.beginPath();
        ctx.ellipse(0, 0, 20, 8, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(56, 189, 248, ${pulse})`;
        ctx.beginPath();
        ctx.ellipse(0, -2, 15, 5, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        if (justPulsed) {
          ctx.strokeStyle = 'rgba(56, 189, 248, 0.7)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(x, y - 14, 18 + (0.3 - st.empTimer) * 40, 0, Math.PI * 2);
          ctx.stroke();
        }
        break;
      }

      case 'intel_uplink': {
        ctx.fillStyle = steelD;
        ctx.fillRect(x - 5, y - 2, 10, 20);
        ctx.fillStyle = steel;
        ctx.fillRect(x - 14, y + 12, 28, 6);

        ctx.save();
        ctx.translate(x, y - 14);
        ctx.rotate(t * 1.4);
        ctx.strokeStyle = 'rgba(168, 85, 247, 0.8)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(18, 0);
        ctx.stroke();
        ctx.fillStyle = 'rgba(168, 85, 247, 0.18)';
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, 18, -0.35, 0.35);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        ctx.fillStyle = '#a855f7';
        ctx.beginPath();
        ctx.arc(x, y - 14, 3, 0, Math.PI * 2);
        ctx.fill();
        break;
      }

      case 'aegis_generator': {
        const base = battlefield.bases[team];
        const ratio = base.maxShields > 0 ? base.shields / base.maxShields : 1;
        const glow = 0.5 + 0.3 * Math.sin(t * 4);

        ctx.fillStyle = steelD;
        ctx.fillRect(x - 10, y + 2, 20, 14);
        ctx.fillStyle = steel;
        ctx.beginPath();
        ctx.moveTo(x, y - 30);
        ctx.lineTo(x + 12, y + 4);
        ctx.lineTo(x - 12, y + 4);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = `rgba(0, 240, 255, ${0.35 + 0.4 * ratio * glow})`;
        ctx.beginPath();
        ctx.moveTo(x, y - 22);
        ctx.lineTo(x + 7, y - 1);
        ctx.lineTo(x - 7, y - 1);
        ctx.closePath();
        ctx.fill();
        break;
      }

      case 'siege_battery': {
        const st = battlefield.buildingSystem?.state?.[team];
        const def = { fireInterval: 20 };
        const justFired = st && st.siegeCooldown !== undefined && (def.fireInterval - st.siegeCooldown) < 0.3;

        ctx.fillStyle = steelD;
        ctx.beginPath();
        ctx.ellipse(x, y + 6, 20, 10, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = steel;
        ctx.beginPath();
        ctx.ellipse(x, y, 16, 12, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = steelL;
        ctx.beginPath();
        ctx.ellipse(x, y - 3, 12, 8, 0, 0, Math.PI * 2);
        ctx.fill();

        const bLen = justFired ? 14 : 18;
        ctx.fillStyle = '#334155';
        ctx.fillRect(x - dir * 2, y - 8, dir * bLen, 5);
        ctx.fillRect(x - dir * 2, y + 2, dir * bLen, 5);
        if (justFired) {
          ctx.fillStyle = '#ffaa00';
          ctx.beginPath();
          ctx.arc(x + dir * bLen, y - 6, 5, 0, Math.PI * 2);
          ctx.arc(x + dir * bLen, y + 4, 5, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
    }

    ctx.restore();
  },

  renderToxinClouds(ctx, battlefield) {
    const clouds = battlefield.buildingSystem?.clouds;
    if (!clouds || clouds.length === 0) return;
    const t = performance.now() * 0.001;

    const gradCanvas = getToxinGradCanvas();
    const gradSize = TOXIN_GRAD_REF_R * 2;
    for (const c of clouds) {
      const fade = Math.min(1, c.life / 1.2); // fade out in the last 1.2s of travel
      const pulse = 0.85 + 0.15 * Math.sin(t * 3 + c.x * 0.01);
      const r = c.radius * pulse;

      // Matches the original circular-gradient-clipped-to-an-ellipse look:
      // uniform scale keeps the gradient's falloff circular, the ellipse
      // clip (not a non-uniform scale) is what flattens the visible shape.
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(c.x, c.y);
      ctx.beginPath();
      ctx.ellipse(0, 0, r, r * 0.6, 0, 0, Math.PI * 2);
      ctx.clip();
      const s = r / TOXIN_GRAD_REF_R;
      ctx.scale(s, s);
      ctx.drawImage(gradCanvas, -TOXIN_GRAD_REF_R, -TOXIN_GRAD_REF_R, gradSize, gradSize);
      ctx.restore();
    }
  },

  // Static Baked Offscreen Canvases for Sul Swarm Pyramidal Bases (Paradigma 7)
  // Static baking of the monumental Sul Swarm Stepped Pyramid (ground shadow, obelisks,
  // 8 tiers, monolith, sanctuary portal frame, statues, apex capstone).
  // Rendered once into _staticBaseCanvas by ensureStaticBases().
  renderSulBaseStatic(ctx, base, mainColor, glowColor) {
    ctx.save();
    const x = base.x;
    const y = base.y;
    const BASE_SCALE = 0.864;
    ctx.translate(x, y);
    ctx.scale(BASE_SCALE, BASE_SCALE);
    ctx.translate(-x, -y);
    const isWestern = base.team === 'western';
    const dir = isWestern ? 1 : -1;

    const groundY = y + 16;
    const centerX = dir === 1 ? x + 58 : x - 58;

    // Populate _sulTierCoordsScratch coordinates mathematically (zero allocation)
    let currentScratchY = groundY;
    for (let i = 0; i < SUL_TIERS.length; i++) {
      const tr = SUL_TIERS[i];
      const tw = tr.w;
      const th = tr.h;
      const td = tr.d;
      const skew = dir * (td * 0.58);
      const yTop = currentScratchY - th;
      currentScratchY -= th;
      const frontLeft = centerX - tw / 2;
      const tc = _sulTierCoordsScratch[i];
      tc.x = frontLeft;
      tc.y = yTop;
      tc.w = tw;
      tc.h = th;
      tc.d = td;
      tc.skew = skew;
    }

    // Palette: Ancient Alien Obsidian / Basalt with 3D Shading, Gold Inlays and Bio-Plasma
    const stoneDark = '#0b0612';
    const stoneMid = '#1e142c';
    const stoneLight = '#33214e';
    const stoneRim = '#503578';
    const goldMid = '#eab308';
    const goldLight = '#fef08a';
    const goldTrim = '#facc15';
    const plasmaGlow = '#d946ef';

    // Helper: Draw single 3D obelisk
    const drawObelisk = (ox, oy, ow, oh, shadowR, shadowRY) => {
      ctx.fillStyle = 'rgba(5, 2, 10, 0.55)';
      ctx.beginPath();
      ctx.ellipse(ox, oy + 2, shadowR, shadowRY, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = stoneDark;
      ctx.beginPath();
      ctx.moveTo(ox - ow / 2, oy);
      ctx.lineTo(ox - ow / 2 + 1.5, oy - oh);
      ctx.lineTo(ox + ow / 2 - 1.5, oy - oh);
      ctx.lineTo(ox + ow / 2, oy);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = stoneMid;
      ctx.fillRect(ox - ow / 2 + 2, oy - oh + 4, ow - 4, oh - 5);

      ctx.fillStyle = stoneDark;
      const obFlankX = dir === 1 ? ox + ow / 2 - 2 : ox - ow / 2;
      ctx.fillRect(obFlankX, oy - oh + 3, 2, oh - 3);

      ctx.fillStyle = goldMid;
      ctx.fillRect(ox - ow / 2 + 1, oy - oh + 2, ow - 2, 2);
      ctx.fillRect(ox - ow / 2 + 1, oy - 6, ow - 2, 1.8);
      ctx.fillStyle = goldLight;
      ctx.fillRect(ox - 1.5, oy - oh + 1, 3, 1.5);

      ctx.fillStyle = plasmaGlow;
      ctx.fillRect(ox - 1.2, oy - oh + 6, 2.4, oh - 14);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(ox - 0.5, oy - oh + 8, 1, oh - 18);
    };

    // 1. Organic Sprawling Ground Shadow
    ctx.fillStyle = 'rgba(8, 3, 16, 0.70)';
    ctx.beginPath();
    ctx.ellipse(centerX, groundY + 4, 88, 20, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(5, 2, 10, 0.45)';
    ctx.beginPath();
    ctx.ellipse(centerX + dir * 42, groundY + 8, 48, 10, 0, 0, Math.PI * 2);
    ctx.ellipse(centerX - dir * 28, groundY + 6, 36, 8, 0, 0, Math.PI * 2);
    ctx.fill();

    // 2. Rear Sentinel Obelisk
    drawObelisk(centerX + dir * 96, groundY - 22, 10, 38, 8, 3.5);

    // 3. True 2.5D Volumetric 8-Tier Stepped Alien Stone Pyramid Scope (+5% scale)
    ctx.save();
    ctx.translate(centerX, groundY);
    ctx.scale(1.05, 1.05);
    ctx.translate(-centerX, -groundY);

    let currentY = groundY;
    for (let i = 0; i < SUL_TIERS.length; i++) {
      const tr = SUL_TIERS[i];
      const tw = tr.w;
      const th = tr.h;
      const td = tr.d;
      const skew = dir * (td * 0.58);

      const yBottom = currentY;
      const yTop = currentY - th;
      currentY -= th;

      const frontLeft = centerX - tw / 2;
      const frontRight = centerX + tw / 2;
      const flankFrontX = dir === 1 ? frontRight : frontLeft;
      const flankBackX = flankFrontX + skew;

      ctx.fillStyle = stoneDark;
      ctx.beginPath();
      ctx.moveTo(flankFrontX, yBottom);
      ctx.lineTo(flankFrontX, yTop);
      ctx.lineTo(flankBackX, yTop - td);
      ctx.lineTo(flankBackX, yBottom - td);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = '#180e28';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(flankFrontX, yTop + th * 0.5);
      ctx.lineTo(flankBackX, yTop + th * 0.5 - td);
      ctx.stroke();

      ctx.fillStyle = (i % 2 === 0) ? stoneLight : '#2c1c44';
      ctx.beginPath();
      ctx.moveTo(frontLeft, yTop);
      ctx.lineTo(frontLeft + skew, yTop - td);
      ctx.lineTo(frontRight + skew, yTop - td);
      ctx.lineTo(frontRight, yTop);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = stoneRim;
      ctx.fillRect(frontLeft, yTop, tw, 1.5);

      if (i % 2 === 1) {
        ctx.fillStyle = goldMid;
        ctx.fillRect(frontLeft + 4, yTop, tw - 8, 1.2);
      }

      ctx.fillStyle = stoneMid;
      ctx.fillRect(frontLeft, yTop + 1, tw, th - 1);

      ctx.fillStyle = stoneDark;
      const seamStep = Math.max(16, Math.floor(tw / 5));
      for (let bx = frontLeft + seamStep; bx < frontRight - 8; bx += seamStep) {
        ctx.fillRect(bx, yTop + 1, 1.5, th - 1);
      }

      ctx.fillStyle = goldTrim;
      ctx.fillRect(flankFrontX - dir * 3.5, yTop - 1, 4.5, 3);
      ctx.fillStyle = goldLight;
      ctx.fillRect(flankFrontX - dir * 2, yTop - 0.5, 2.5, 1.5);

      ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.fillRect(frontLeft, yBottom - 1.5, tw, 1.5);
    }

    // Monolith body (Stufen 1-7)
    const monoBottomW = 32;
    const monoTopW = 18;
    const monoBottomY = groundY - SUL_TIERS[0].h;
    const monoTopY = _sulTierCoordsScratch[6].y;
    const monoFlankW = 3.5;

    ctx.fillStyle = stoneDark;
    ctx.beginPath();
    ctx.moveTo(centerX + dir * (monoBottomW / 2), monoBottomY);
    ctx.lineTo(centerX + dir * (monoTopW / 2), monoTopY);
    ctx.lineTo(centerX + dir * (monoTopW / 2 + monoFlankW), monoTopY - 2);
    ctx.lineTo(centerX + dir * (monoBottomW / 2 + monoFlankW), monoBottomY - 2);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#26193a';
    ctx.beginPath();
    ctx.moveTo(centerX - monoBottomW / 2, monoBottomY);
    ctx.lineTo(centerX - monoTopW / 2, monoTopY);
    ctx.lineTo(centerX + monoTopW / 2, monoTopY);
    ctx.lineTo(centerX + monoBottomW / 2, monoBottomY);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = goldMid;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(centerX - monoBottomW / 2 + 1, monoBottomY);
    ctx.lineTo(centerX - monoTopW / 2 + 1, monoTopY);
    ctx.moveTo(centerX + monoBottomW / 2 - 1, monoBottomY);
    ctx.lineTo(centerX + monoTopW / 2 - 1, monoTopY);
    ctx.stroke();

    // Ground Entrance Sanctuary Portal Frame
    const portalW = 20;
    const portalH = 18;
    const portalX = centerX - portalW / 2;
    const portalY = groundY - portalH;

    ctx.fillStyle = stoneDark;
    ctx.fillRect(portalX - 3, portalY - 2, portalW + 6, portalH + 2);
    ctx.fillStyle = stoneMid;
    ctx.fillRect(portalX - 2, portalY - 2, portalW + 4, 3);

    ctx.strokeStyle = goldMid;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(portalX - 1, portalY + portalH);
    ctx.lineTo(portalX - 1, portalY - 1);
    ctx.lineTo(portalX + portalW + 1, portalY - 1);
    ctx.lineTo(portalX + portalW + 1, portalY + portalH);
    ctx.stroke();

    ctx.fillStyle = '#050209';
    ctx.fillRect(portalX, portalY, portalW, portalH);

    // Static Sul Portal Glow & Ground Bio-Cables (Baked into _staticBaseCanvas)
    const portalTex = getSulPortalGradCanvas();
    const portalR = 16;
    ctx.save();
    ctx.translate(centerX, groundY - 6);
    ctx.beginPath();
    ctx.ellipse(0, 0, 10, 12, 0, 0, Math.PI * 2);
    ctx.clip();
    const portalS = portalR / SUL_PORTAL_REF_R;
    ctx.scale(portalS, portalS);
    ctx.drawImage(portalTex, -SUL_PORTAL_REF_R, -SUL_PORTAL_REF_R, SUL_PORTAL_REF_R * 2, SUL_PORTAL_REF_R * 2);
    ctx.restore();

    // Neural bio-cables crawling out of the portal across the ground plane
    ctx.strokeStyle = '#1e0722';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(centerX, groundY - 2);
    ctx.quadraticCurveTo(centerX + dir * 16, groundY + 4, centerX + dir * 36, groundY + 7);
    ctx.moveTo(centerX - 3, groundY - 2);
    ctx.quadraticCurveTo(centerX - dir * 14, groundY + 3, centerX - dir * 28, groundY + 6);
    ctx.stroke();

    ctx.strokeStyle = plasmaGlow;
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(centerX, groundY - 2);
    ctx.quadraticCurveTo(centerX + dir * 16, groundY + 4, centerX + dir * 36, groundY + 7);
    ctx.stroke();

    // Bio-Carapace Honeycomb Clusters (Hive Cells) base background on Flanks of Tiers 2-5
    const rootDark = '#38061a';
    const rootMid = '#701a75';
    const rootLight = '#a21caf';
    ctx.fillStyle = rootDark;
    for (let h = 1; h <= 3; h++) {
      const tc = _sulTierCoordsScratch[h];
      ctx.beginPath();
      ctx.ellipse(tc.x + 8, tc.y + tc.h / 2, 7, 5, 0, 0, Math.PI * 2);
      ctx.ellipse(tc.x + tc.w - 8, tc.y + tc.h / 2, 7, 5, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Creeping Biomechanical Roots & Tendrils with 3D Depth
    ctx.strokeStyle = rootDark;
    ctx.lineWidth = 3.5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(_sulTierCoordsScratch[3].x + 4, _sulTierCoordsScratch[3].y);
    ctx.quadraticCurveTo(_sulTierCoordsScratch[1].x - 6, _sulTierCoordsScratch[1].y + 4, _sulTierCoordsScratch[0].x - 10, groundY + 2);
    ctx.moveTo(_sulTierCoordsScratch[4].x + _sulTierCoordsScratch[4].w - 4, _sulTierCoordsScratch[4].y);
    ctx.quadraticCurveTo(_sulTierCoordsScratch[1].x + _sulTierCoordsScratch[1].w + 8, _sulTierCoordsScratch[1].y + 4, _sulTierCoordsScratch[0].x + _sulTierCoordsScratch[0].w + 12, groundY + 3);
    ctx.stroke();

    ctx.strokeStyle = rootMid;
    ctx.lineWidth = 2.0;
    ctx.stroke();

    ctx.strokeStyle = rootLight;
    ctx.lineWidth = 1.0;
    ctx.stroke();

    // Guardian Falcon Statues
    for (let si = 0; si < SUL_STATUE_OFFSETS.length; si++) {
      const so = SUL_STATUE_OFFSETS[si];
      const sx = centerX + so;
      const sy = groundY - 14;

      ctx.fillStyle = stoneDark;
      ctx.fillRect(sx - 4, groundY - 4, 8, 4);
      ctx.fillStyle = stoneRim;
      ctx.fillRect(sx - 4, groundY - 4, 8, 1);

      ctx.fillStyle = stoneMid;
      ctx.beginPath();
      ctx.moveTo(sx - 3, groundY - 4);
      ctx.lineTo(sx - 4, sy + 4);
      ctx.lineTo(sx, sy);
      ctx.lineTo(sx + 4, sy + 4);
      ctx.lineTo(sx + 3, groundY - 4);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = goldLight;
      ctx.fillRect(sx - 2, sy - 1, 4, 2);
      ctx.fillStyle = goldMid;
      ctx.fillRect(sx - 3.5, sy + 2, 1.5, 6);
      ctx.fillRect(sx + 2, sy + 2, 1.5, 6);

      ctx.fillStyle = plasmaGlow;
      ctx.beginPath();
      ctx.arc(sx, sy + 4, 1.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Sharp 3D Pyramidion Apex Capstone
    const apexPedestal = _sulTierCoordsScratch[7];
    const capBaseW = 28;
    const capH = 22;
    const apexX = centerX;
    const apexTipY = apexPedestal.y - capH;
    const capLeftX = apexX - capBaseW / 2;
    const capRightX = apexX + capBaseW / 2;
    const capBaseY = apexPedestal.y;

    ctx.fillStyle = stoneDark;
    ctx.beginPath();
    ctx.moveTo(apexX, apexTipY);
    ctx.lineTo(dir === 1 ? capRightX : capLeftX, capBaseY);
    ctx.lineTo(apexX, capBaseY);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#26183a';
    ctx.beginPath();
    ctx.moveTo(apexX, apexTipY);
    ctx.lineTo(dir === 1 ? capLeftX : capRightX, capBaseY);
    ctx.lineTo(apexX, capBaseY);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = goldMid;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(capLeftX, capBaseY);
    ctx.lineTo(apexX, apexTipY);
    ctx.lineTo(capRightX, capBaseY);
    ctx.moveTo(apexX, capBaseY);
    ctx.lineTo(apexX, apexTipY);
    ctx.stroke();

    ctx.fillStyle = goldLight;
    ctx.beginPath();
    ctx.moveTo(apexX, apexTipY - 2);
    ctx.lineTo(apexX - 2.5, apexTipY + 4);
    ctx.lineTo(apexX + 2.5, apexTipY + 4);
    ctx.closePath();
    ctx.fill();

    ctx.restore(); // Closes 3D Pyramid Scope

    // 4. Foreground Alien Obelisk
    drawObelisk(centerX + dir * 66, groundY + 22, 13, 54, 11, 4.5);

    ctx.restore(); // Closes main base transform
  },

  // 8-Bit Volumetric Sul Swarm Monumental Alien Stepped Pyramid (True 2.5D Volumetric Geometry, Gold Inlays & Apex Plasma Sphere)
  renderSulBase(ctx, base, mainColor, glowColor) {
    if (!this._staticBasePrimed) {
      this.renderSulBaseStatic(ctx, base, mainColor, glowColor);
    }

    ctx.save();
    const x = base.x;
    const y = base.y;
    const BASE_SCALE = 0.864;
    ctx.translate(x, y);
    ctx.scale(BASE_SCALE, BASE_SCALE);
    ctx.translate(-x, -y);
    const isWestern = base.team === 'western';
    const dir = isWestern ? 1 : -1;
    const t = performance.now() * 0.001;
    const isLow = this.quality === 'low';

    const groundY = y + 16; // Shifted 10px up so Sul base visually aligns with human base
    const centerX = dir === 1 ? x + 58 : x - 58; // Center shifted for deep 3D perspective

    // Populate _sulTierCoordsScratch coordinates mathematically (zero allocation)
    let currentScratchY = groundY;
    for (let i = 0; i < SUL_TIERS.length; i++) {
      const tr = SUL_TIERS[i];
      const tw = tr.w;
      const th = tr.h;
      const td = tr.d;
      const skew = dir * (td * 0.58);
      const yTop = currentScratchY - th;
      currentScratchY -= th;
      const frontLeft = centerX - tw / 2;
      const tc = _sulTierCoordsScratch[i];
      tc.x = frontLeft;
      tc.y = yTop;
      tc.w = tw;
      tc.h = th;
      tc.d = td;
      tc.skew = skew;
    }

    const rootDark = '#38061a';
    const rootMid = '#701a75';
    const rootLight = '#a21caf';
    const goldTrim = '#facc15';
    const plasmaGlow = '#d946ef';

    // 3. True 2.5D Volumetric 8-Tier Stepped Alien Stone Pyramid Scope (+5% scale)
    ctx.save();
    ctx.translate(centerX, groundY);
    ctx.scale(1.05, 1.05);
    ctx.translate(-centerX, -groundY);

    // 4. Bio-Carapace Honeycomb Clusters (Hive Cells) dynamic pulses on Flanks of Tiers 2-5
    // Base dark carapace is pre-baked in renderSulBaseStatic; only pulsing cell openings animate per-frame
    for (let h = 1; h <= 3; h++) {
      const tc = _sulTierCoordsScratch[h];
      const hPulse = 0.85 + 0.15 * Math.sin(t * 0.9 + (h - 1));
      ctx.fillStyle = `rgba(217, 70, 239, ${0.45 * hPulse})`;
      for (let c = -1; c <= 1; c++) {
        ctx.fillRect(tc.x + 6 + c * 3, tc.y + tc.h / 2 - 2, 2, 2);
        ctx.fillRect(tc.x + tc.w - 10 + c * 3, tc.y + tc.h / 2 - 2, 2, 2);
      }
    }

    // 5. 20% Diffuse Transluminescent Living Organic Matter (Sul Swarm Bio-Matrix / Creep)
    // Diffusely blankets ~20% of the pyramid masonry in living, breathing, transluminescent tissue.
    for (let pIdx = 0; pIdx < SUL_BIOMASS_PATCH_DEFS.length; pIdx++) {
      const patch = SUL_BIOMASS_PATCH_DEFS[pIdx];
      const tc = _sulTierCoordsScratch[patch.tierIdx];
      let pcx = 0;
      if (patch.rel === 'left') pcx = tc.x + patch.offsetX;
      else if (patch.rel === 'right') pcx = tc.x + tc.w + patch.offsetX;
      else pcx = centerX + dir * patch.offsetX;
      const pcy = tc.y + patch.offsetY;
      // Respiration / biological heartbeat pulse (calm, sovereign breathing cycle)
      const pulseRate = 0.85;
      const respire = Math.sin(t * pulseRate + patch.seed);
      const alphaPulse = 0.44 + 0.14 * respire;
      const scalePulse = 1.0 + 0.04 * respire;

      ctx.save();
      ctx.translate(pcx, pcy);
      ctx.scale(scalePulse, scalePulse);

      // A. Diffuse Organic Glow & Transluminescent Subsurface Scatter
      // Baked LUT texture (Paradigm 8): drawn directly into the elliptical bounding box
      // (-rx, -ry, rx*2, ry*2) without an expensive per-patch stencil-buffer ctx.clip().
      const subRx = patch.rx * 1.35;
      const subRy = patch.ry * 1.35;
      const subGlowTex = getSulSubGlowGradCanvas();
      ctx.globalAlpha = alphaPulse;
      ctx.drawImage(subGlowTex, -subRx, -subRy, subRx * 2, subRy * 2);

      // B. Transluminescent Biomass Lobes (Multi-lobed organic tissue)
      // Direct positioned drawImage without per-lobe save/translate/scale/restore.
      const lobeTex = getSulLobeGradCanvas();
      for (const lobe of patch.lobes) {
        const lr = lobe.r * (0.96 + 0.04 * Math.sin(t * 1.1 + lobe.dx));
        ctx.drawImage(lobeTex, lobe.dx - lr, lobe.dy - lr, lr * 2, lr * 2);
      }
      ctx.globalAlpha = 1.0;

      // C. Subcutaneous Bio-Vascular Vessels / Living Capillaries (batched path per patch)
      if (!isLow && patch.vessels.length > 0) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        let hasSegments = false;
        for (const vPath of patch.vessels) {
          if (vPath.length < 2) continue;
          hasSegments = true;
          ctx.moveTo(vPath[0].x, vPath[0].y);
          for (let vi = 1; vi < vPath.length; vi++) {
            ctx.lineTo(vPath[vi].x, vPath[vi].y);
          }
        }
        if (hasSegments) {
          ctx.lineWidth = 1.2;
          ctx.strokeStyle = `rgba(255, 255, 255, ${0.60 * alphaPulse})`;
          ctx.stroke();

          // Outer glowing vessel sheath
          ctx.strokeStyle = `rgba(236, 72, 153, ${0.40 * alphaPulse})`;
          ctx.lineWidth = 2.4;
          ctx.stroke();
        }
      }

      // D. Living Bioluminescent Spores / Pores
      for (const sp of patch.spores) {
        const spPulse = Math.sin(t * 1.3 + sp.p);
        const spAlpha = Math.max(0.1, 0.55 + 0.45 * spPulse);
        const spR = 1.2 + 0.6 * spPulse;

        // Soft halo
        ctx.fillStyle = `rgba(217, 70, 239, ${0.45 * spAlpha})`;
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, spR * 2.2, 0, Math.PI * 2);
        ctx.fill();

        // Hot white/gold bioluminescent pore
        ctx.fillStyle = `rgba(255, 255, 255, ${spAlpha})`;
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, spR, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    }

    const monoTopY = _sulTierCoordsScratch[6].y;

    // Glowing Alien Hieroglyphs / Cartouches on the Central Monolith
    const glyphCount = 5;
    for (let g = 0; g < glyphCount; g++) {
      const gy = monoTopY + 6 + g * 8;
      const gPulse = 0.75 + 0.25 * Math.sin(t * 1.0 + g * 0.8);
      // Alternating gold and violet glyphs
      ctx.fillStyle = (g % 2 === 0) ? goldTrim : `rgba(217, 70, 239, ${gPulse})`;
      if (g === 0) {
        // Upper cartouche / crescent glyph
        ctx.fillRect(centerX - 4, gy, 8, 2);
        ctx.fillRect(centerX - 2, gy + 2, 4, 1.5);
      } else if (g === 1) {
        // Sul loaf hieroglyph 𓏏
        ctx.fillRect(centerX - 5, gy, 10, 2.5);
        ctx.fillRect(centerX - 2, gy + 2.5, 4, 2);
      } else if (g === 2) {
        // Anchor / Ankh-like energy glyph
        ctx.fillRect(centerX - 1, gy, 2, 6);
        ctx.fillRect(centerX - 4, gy + 2, 8, 1.5);
        ctx.fillRect(centerX - 3, gy + 4.5, 6, 1.5);
      } else if (g === 3) {
        // Sacred hash bars
        ctx.fillRect(centerX - 4, gy, 8, 1.5);
        ctx.fillRect(centerX - 3, gy + 2.5, 6, 1.5);
      } else {
        // Lower power knot
        ctx.fillRect(centerX - 2, gy, 4, 3);
      }
    }

    const apexPedestal = _sulTierCoordsScratch[7]; // Tier 8
    const capH = 22;
    const apexX = centerX;
    const apexTipY = apexPedestal.y - capH;

    // 10. The Dense Enveloping Energy Sphere with Animated Charge-Up
    // Completely wraps around the sharp apex point
    const sphereCenterY = apexTipY + 4;

    // Calculate charge-up progress (0.0 to 1.0)
    // Snappy (400ms duration) yet clearly visible and animated
    const chargeDuration = 0.40;
    let chargeProgress = 0;
    if (base.targetEnemy) {
      if (base.attackCooldown <= chargeDuration) {
        chargeProgress = Math.max(0, Math.min(1.0, 1.0 - (base.attackCooldown / chargeDuration)));
      }
    }
    const isFiring = (base.recoilTimer || 0) > 0.05;

    // Idle breathing vs. charging excitation vs. recoil kick (ruhig, erhaben & überlegen)
    const idlePulse = Math.sin(t * 1.1) * 1.0;
    const chargeJitter = chargeProgress > 0 ? (Math.sin(t * 18.0) * chargeProgress * 1.4) : 0;
    const sphereR = 14 + idlePulse + (chargeProgress * 5.0) + chargeJitter;

    // A0. Atmospheric Heat Shimmer & Blur Effect (Optischer Hitzeschleier & Luftflimmern)
    // Envelops the apex, the energy sphere, and upper pyramid tiers in shimmering heat distortion.
    if (!isLow) {
      ctx.save();
      const heatIntensity = 0.85 + (chargeProgress * 0.45);
      const heatFlicker = Math.sin(t * 2.4) * 0.05;

      // 1. Broad Diffuse Thermal Blur Wash (Softer optischer Hitzeschleier über den oberen Stufen)
      const hazeRadiusX = 46 + (chargeProgress * 10);
      const hazeRadiusY = 38 + (chargeProgress * 8);
      const heatAlpha = (0.30 + heatFlicker) * heatIntensity;
      if (heatAlpha > 0.01) {
        ctx.save();
        ctx.globalAlpha = heatAlpha;
        const heatTex = getSulHeatGradCanvas();
        ctx.drawImage(heatTex, apexX - hazeRadiusX, sphereCenterY + 4 - hazeRadiusY, hazeRadiusX * 2, hazeRadiusY * 2);
        ctx.restore();
      }

      // 2. Thermal Shimmer on Upper Pyramid Masonry (Tier 7 & 8 optische Unschärfe-Überlagerung)
      const shimmerJitter = Math.sin(t * 3.0) * 0.75;
      ctx.fillStyle = `rgba(253, 230, 138, ${0.14 * heatIntensity})`;
      ctx.beginPath();
      ctx.moveTo(apexPedestal.x + shimmerJitter, apexPedestal.y);
      ctx.lineTo(apexPedestal.x + apexPedestal.w + shimmerJitter, apexPedestal.y);
      ctx.lineTo(apexX + 16, apexTipY);
      ctx.lineTo(apexX - 16, apexTipY);
      ctx.closePath();
      ctx.fill();

      // 3. Rising Convection Mirage Waves (Aufsteigende Hitzewellen / Luftflimmern)
      ctx.lineWidth = 2.0;
      ctx.lineCap = 'round';
      for (let w = 0; w < 4; w++) {
        const waveProgress = ((t * 0.55 + w * 0.25) % 1.0);
        const waveY = sphereCenterY + 12 - (waveProgress * 38);
        const waveAlpha = Math.sin(waveProgress * Math.PI) * (0.28 * heatIntensity);
        const waveWobble = Math.sin(t * 2.6 + w * 2.1) * 2.2;
        const waveSpan = 18 + waveProgress * 14;

        ctx.strokeStyle = `rgba(254, 215, 170, ${waveAlpha})`;
        ctx.beginPath();
        ctx.moveTo(apexX - waveSpan + waveWobble, waveY);
        ctx.quadraticCurveTo(apexX + waveWobble * 0.5, waveY - 3, apexX + waveSpan + waveWobble, waveY);
        ctx.stroke();

        ctx.strokeStyle = `rgba(244, 114, 182, ${waveAlpha * 0.6})`;
        ctx.beginPath();
        ctx.moveTo(apexX - waveSpan * 0.8 - waveWobble, waveY + 2);
        ctx.quadraticCurveTo(apexX - waveWobble * 0.5, waveY - 1, apexX + waveSpan * 0.8 - waveWobble, waveY + 2);
        ctx.stroke();
      }

      // 4. Concentric Thermal Refraction Ripples (Optische Verzerrungsringe um die Kugel)
      for (let r = 0; r < 2; r++) {
        const rProg = ((t * 0.45 + r * 0.5) % 1.0);
        const rRadius = sphereR + 4 + (rProg * 22);
        const rAlpha = (1.0 - rProg) * 0.22 * heatIntensity;
        ctx.strokeStyle = `rgba(254, 240, 138, ${rAlpha})`;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.arc(apexX, sphereCenterY, rRadius, 0, Math.PI * 2);
        ctx.stroke();
      }

      ctx.restore();
    }

    // A. Outer atmospheric ionization corona
    if (!isLow) {
      const coronaR = sphereR * (1.8 + chargeProgress * 0.5);
      const coronaAlpha = 0.35 + chargeProgress * 0.45;
      if (coronaAlpha > 0.01) {
        ctx.save();
        ctx.globalAlpha = coronaAlpha;
        const coronaTex = getSulCoronaGradCanvas();
        ctx.drawImage(coronaTex, apexX - coronaR, sphereCenterY - coronaR, coronaR * 2, coronaR * 2);
        ctx.restore();
      }
    }

    // B. Inward-Spiraling Charge Filaments (Visible when charging up to fire)
    if (chargeProgress > 0.05) {
      ctx.save();
      ctx.translate(apexX, sphereCenterY);
      ctx.rotate(-t * (2.2 + chargeProgress * 4.5));
      const inRadius = (1.0 - chargeProgress * 0.75) * 26;
      ctx.strokeStyle = chargeProgress > 0.6 ? '#ffffff' : '#fef08a';
      ctx.lineWidth = 1.5;
      for (let arm = 0; arm < 4; arm++) {
        ctx.rotate((Math.PI * 2) / 4);
        ctx.beginPath();
        ctx.moveTo(inRadius, 0);
        ctx.quadraticCurveTo(inRadius * 0.5, inRadius * 0.3, 0, 0);
        ctx.stroke();
      }
      ctx.restore();
    }

    // C. Swirling Plasma Filament Mantle (Rotates continuously)
    ctx.save();
    ctx.translate(apexX, sphereCenterY);
    ctx.rotate(t * 0.7);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.lineWidth = 1.5;
    for (let f = 0; f < 3; f++) {
      ctx.rotate((Math.PI * 2) / 3);
      ctx.beginPath();
      ctx.arc(0, 0, sphereR * 0.8, 0, Math.PI * 0.5);
      ctx.stroke();
    }
    ctx.restore();

    // D. Dense Bio-Plasma Sphere Body
    const sphereTex = getSulApexSphereGradCanvas(chargeProgress > 0.5);
    ctx.drawImage(sphereTex, apexX - sphereR, sphereCenterY - sphereR, sphereR * 2, sphereR * 2);

    // E. Intense Singular Hot Core
    const coreR = 5 + (chargeProgress * 3.0);
    ctx.fillStyle = chargeProgress > 0.4 ? '#ffffff' : '#fef08a';
    ctx.beginPath();
    ctx.arc(apexX, sphereCenterY, coreR, 0, Math.PI * 2);
    ctx.fill();

    // F. Micro-Lightning Arcs leaping from the gold needle-point
    if (!isLow && (Math.sin(t * 2.5) > 0.45 || chargeProgress > 0.3)) {
      ctx.strokeStyle = chargeProgress > 0.5 ? '#fef08a' : '#f5d0fe';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(apexX, apexTipY);
      const lMidX = apexX + Math.sin(t * 4.5) * 6;
      const lMidY = sphereCenterY + Math.cos(t * 5.0) * 5;
      ctx.lineTo(lMidX, lMidY);
      ctx.lineTo(apexX + Math.cos(t * 5.5) * 9, sphereCenterY + Math.sin(t * 5.5) * 9);
      ctx.stroke();
    }

    // G. Discharge Flash / Shockwave when firing
    if (isFiring) {
      const shockR = (0.28 - (base.recoilTimer || 0)) * 90;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.lineWidth = 2.0;
      ctx.beginPath();
      ctx.arc(apexX, sphereCenterY, Math.max(4, shockR), 0, Math.PI * 2);
      ctx.stroke();

      ctx.strokeStyle = 'rgba(217, 70, 239, 0.6)';
      ctx.lineWidth = 3.5;
      ctx.beginPath();
      ctx.arc(apexX, sphereCenterY, Math.max(6, shockR * 0.8), 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore(); // Closes 3. Pyramid Scope (+5% scale)
    ctx.restore(); // Closes main base transform
  },

  // Swarm Pyramidal Apex Defense Targeting System (schießt direkt aus der Pyramidenspitze)
  renderSulBaseTurret(ctx, battlefield, base, mainColor, glowColor) {
    ctx.save();
    const isWestern = base.team === 'western';
    const dir = isWestern ? 1 : -1;
    const isLow = this.quality === 'low';

    const x = base.x;
    const y = base.y;
    const BASE_SCALE = 0.864;
    ctx.translate(x, y);
    ctx.scale(BASE_SCALE, BASE_SCALE);
    ctx.translate(-x, -y);

    const groundY = y + 16; // Shifted 10px up (originally y + 26) to match renderSulBase
    const centerX = dir === 1 ? x + 58 : x - 58;
    const apexX = centerX;
    const apexY = groundY - 92; // Center of the apex energy sphere (scaled +5%)

    // Sentry / Target tracking
    let aimDir;
    if (base.targetEnemy) {
      aimDir = Math.atan2(base.targetEnemy.y - apexY, base.targetEnemy.x - apexX);
      base._gunTargetAngle = aimDir;
      if (base._gunCurrentAngle === undefined) base._gunCurrentAngle = aimDir;
      // Wrap to shortest rotational path - same fix as base.turretAngle in
      // battlefield.js updateBaseDefense(), needed here too since this is an
      // independent lerp of the same eastern-base wrap-boundary angle.
      let gunAngleDiff = aimDir - base._gunCurrentAngle;
      gunAngleDiff = ((gunAngleDiff + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
      base._gunCurrentAngle += gunAngleDiff * 0.18;
      base._gunCurrentAngle = ((base._gunCurrentAngle + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
      aimDir = base._gunCurrentAngle;
    } else {
      const defaultAngle = dir === 1 ? 0 : Math.PI;
      aimDir = defaultAngle;
      base._gunCurrentAngle = defaultAngle;
    }

    const isFiring = (base.recoilTimer || 0) > 0.05;

    // Draw directional apex defense focus
    ctx.save();
    ctx.translate(apexX, apexY);
    ctx.rotate(aimDir);

    // Directional targeting lightning arcs focused towards the enemy
    if (base.targetEnemy || isFiring) {
      const beamLen = isFiring ? 24 : 14;

      // 1. Central directional plasma beam
      ctx.strokeStyle = isFiring ? '#ffffff' : '#d946ef';
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.moveTo(8, 0);
      ctx.lineTo(8 + beamLen, 0);
      ctx.stroke();

      // 2. Flanking plasma spikes (top & bottom)
      ctx.strokeStyle = '#fef08a';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(8, -3);
      ctx.lineTo(8 + beamLen * 0.7, -5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(8, 3);
      ctx.lineTo(8 + beamLen * 0.7, 5);
      ctx.stroke();

      // 3. Electric vector arcs
      ctx.strokeStyle = '#e879f9';
      ctx.lineWidth = 1.0;
      for (let a = -1; a <= 1; a += 2) {
        ctx.beginPath();
        ctx.moveTo(10, a * 2);
        ctx.lineTo(16, a * 6);
        ctx.lineTo(22, a * 4);
        ctx.stroke();
      }

      // 4. Muzzle spark & energy discharge node
      ctx.fillStyle = isFiring ? '#ffffff' : '#facc15';
      ctx.beginPath();
      ctx.arc(8 + beamLen, 0, isFiring ? 3.5 : 1.5, 0, Math.PI * 2);
      ctx.fill();

      // Additional corona charge nodes
      for (let n = 0; n < 4; n++) {
        const ang = (n * Math.PI) / 2;
        ctx.fillStyle = n % 2 === 0 ? '#fef08a' : '#d946ef';
        ctx.beginPath();
        ctx.arc(Math.cos(ang) * 12, Math.sin(ang) * 12, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }

      // 5. Focusing plasma rings
      for (let r = 0; r < 3; r++) {
        const ringDist = 10 + r * 5;
        ctx.strokeStyle = `rgba(217, 70, 239, ${0.8 - r * 0.2})`;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(ringDist, 0, 3 + r * 1.5, -Math.PI / 3, Math.PI / 3);
        ctx.stroke();
      }

      // 6. Recoil burst sparks when firing
      if (isFiring) {
        for (let s = 0; s < 5; s++) {
          const sAngle = (s - 2) * 0.25;
          ctx.strokeStyle = s % 2 === 0 ? '#ffffff' : '#fef08a';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(12, 0);
          ctx.lineTo(12 + Math.cos(sAngle) * (beamLen + 6), Math.sin(sAngle) * (beamLen + 6));
          ctx.stroke();
        }
      }
    }

    ctx.restore();
    ctx.restore();
  }
};
